import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Trash2, Bot, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function RobotsManager() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // New Rule Form
  const [userAgent, setUserAgent] = useState("*");
  const [directive, setDirective] = useState("Disallow");
  const [path, setPath] = useState("/");

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const res = await axios.get(`${API}/seo/robots`, { withCredentials: true });
      setRules(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch robots.txt rules");
    } finally {
      setLoading(false);
    }
  };

  const handleAddRule = async () => {
    if (!userAgent || !path) return toast.error("User Agent and Path are required");
    
    setSaving(true);
    try {
      await axios.post(`${API}/seo/robots`, {
        user_agent: userAgent,
        directive: directive,
        path: path
      }, { withCredentials: true });
      
      toast.success("Rule added successfully!");
      setPath("/");
      fetchRules();
    } catch (err) {
      console.error(err);
      toast.error("Failed to add rule");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (id) => {
    try {
      await axios.delete(`${API}/seo/robots/${id}`, { withCredentials: true });
      toast.success("Rule deleted");
      fetchRules();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete rule");
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600 dark:text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Bot className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            Robots.txt Manager
          </h3>
          <p className="text-sm text-gray-500 dark:text-[#94A3B8]">
            Control which pages Google and other search engines are allowed to crawl.
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => window.open(`${API.replace('/api', '')}/robots.txt`, '_blank')}
            variant="outline"
            className="border-gray-200 dark:border-white/10 text-gray-900 dark:text-white hover:bg-gray-100 dark:bg-white/5 rounded-xl"
          >
            <ExternalLink className="w-4 h-4 mr-2" /> View robots.txt
          </Button>
        </div>
      </div>

      <div className="bg-white dark:bg-[#0B101A] border border-gray-100 dark:border-white/[0.05] rounded-xl p-5">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Add Custom Rule</h4>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="w-full md:w-48 space-y-1.5">
            <label className="text-xs text-gray-500 dark:text-[#94A3B8]">User Agent</label>
            <Input 
              value={userAgent}
              onChange={e => setUserAgent(e.target.value)}
              placeholder="* (All Bots)"
              className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white rounded-xl"
            />
          </div>
          <div className="w-full md:w-32 space-y-1.5">
            <label className="text-xs text-gray-500 dark:text-[#94A3B8]">Directive</label>
            <Select value={directive} onValueChange={setDirective}>
              <SelectTrigger className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white rounded-xl">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-[#0B101A] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white">
                <SelectItem value="Allow">Allow</SelectItem>
                <SelectItem value="Disallow">Disallow</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 space-y-1.5">
            <label className="text-xs text-gray-500 dark:text-[#94A3B8]">URL Path</label>
            <Input 
              value={path}
              onChange={e => setPath(e.target.value)}
              placeholder="/admin-login"
              className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white rounded-xl"
            />
          </div>
          
          <Button 
            onClick={handleAddRule} 
            disabled={saving}
            className="w-full md:w-auto bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl shadow-xs"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Rule"}
          </Button>
        </div>
      </div>

      <div className="bg-white dark:bg-[#0B101A] border border-gray-100 dark:border-white/[0.05] rounded-xl overflow-hidden">
        {rules.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-[#94A3B8]">
            No robots.txt rules added yet.
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-white/[0.02] border-b border-gray-100 dark:border-white/[0.05]">
              <tr>
                <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8]">User Agent</th>
                <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8]">Directive</th>
                <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8]">Path</th>
                <th className="p-4 font-medium text-gray-500 dark:text-[#94A3B8] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.05]">
              {rules.map((r, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:bg-white/[0.01]">
                  <td className="p-4 text-gray-900 dark:text-white font-mono text-xs">{r.user_agent}</td>
                  <td className="p-4">
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold border ${r.directive === 'Allow' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800' : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800'}`}>
                      {r.directive}
                    </span>
                  </td>
                  <td className="p-4 text-gray-900 dark:text-white font-mono text-xs">{r.path}</td>
                  
                  <td className="p-4 text-right">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeleteRule(r.rule_id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
